import { APP_FAVICON } from './constants';

const CACHE_KEY_PREFIX = 'minetech.head.';

const FAVICON_MIME: Record<string, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  ico: 'image/x-icon',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export interface TenantHead {
  title: string;
  favicon: string;
  appName?: string;
}

const cacheKey = (hostname: string) => CACHE_KEY_PREFIX + hostname;

function currentHost(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

function setFavicon(href: string): void {
  const existing = document.querySelectorAll('link[rel~="icon"]');
  const only = existing.length === 1 ? existing[0] : undefined;
  if (only?.getAttribute('href') === href) return;
  existing.forEach((element) => element.remove());

  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;

  const extension = href.split('.').pop()?.toLowerCase() ?? '';
  const mime = FAVICON_MIME[extension];
  if (mime) link.type = mime;

  document.head.appendChild(link);
}

function setAppleTitle(appName: string): void {
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]',
  );
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'apple-mobile-web-app-title';
    document.head.appendChild(meta);
  }
  if (meta.content !== appName) meta.content = appName;
}

function pointManifestAtTenant(): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  const href = '/core/api/v1/public/manifest';
  if (link) {
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    return;
  }
  const created = document.createElement('link');
  created.rel = 'manifest';
  created.href = href;
  document.head.appendChild(created);
}

export function applyTenantHead({ title, favicon, appName }: TenantHead): void {
  if (typeof document === 'undefined') return;

  if (title.trim()) document.title = title;
  // Always set: skipping when empty would leave a removed tenant favicon in
  // the head until the next reload.
  setFavicon(favicon.trim() || APP_FAVICON);
  if (appName?.trim()) setAppleTitle(appName);
  pointManifestAtTenant();

  try {
    localStorage.setItem(
      cacheKey(currentHost()),
      JSON.stringify({ title, favicon, appName }),
    );
  } catch {
    // Private browsing or a full quota; the next load just applies a moment late.
  }
}

export function bootTenantHead(): void {
  if (typeof window === 'undefined') return;

  // Unconditional: the install dialog must carry the tenant's name on a first
  // visit too, before anything has been cached.
  pointManifestAtTenant();

  try {
    const raw = localStorage.getItem(cacheKey(currentHost()));
    if (!raw) return;
    const cached = JSON.parse(raw) as unknown;
    const { title, favicon, appName } = cached as Partial<TenantHead>;
    if (title || favicon) {
      applyTenantHead({
        title: title ?? '',
        favicon: favicon ?? '',
        ...(appName ? { appName } : {}),
      });
    }
  } catch {
    // A malformed entry is not worth blocking boot over.
  }
}
